// Wire contract for the MIGRATED APG ORION roll run (CR-20260910-001 milestone 1, contract A1 v0.2).
//
// These mirror the FROZEN backend DTOs of the foundation pass (ets.platform:
// ETS.ShellService.Shell.Protocol/Operations/ApgOrion/RunApgOrionSkill.cs and
// /Dto/ApgOrion/ApgOrionCardsReadyDto.cs). Field names are camelCase — the shell-router COMMAND
// path serializes with plain JSON.stringify (NO class-transformer) and the backend Newtonsoft
// deserializes case-insensitively into the PascalCase C# members; the inbound ESS push arrives
// camelCase on the FE. Do NOT add class-transformer decorators. Same rules as the two copilots'
// operations files (`hedging-grid/copilot/copilot-operations.ts`,
// `copilot-shared/opg-copilot-operations.ts`).
//
// Send: RunApgOrionSkill (command). Receive: ApgOrionCardsReadyDto over ESS, correlated by runId.
//
// ⚠ THERE IS NO CANCEL COMMAND AND NO STOP CONTROL (ruling R-D). Running again supersedes the run
// in flight, and the displaced run receives `superseded` exactly once.
//
// ⚠ THERE IS NO TopN FIELD, deliberately (ruling R-B). Every window date the run calculates is
// delivered; the "show top N" setting is a DISPLAY limit the panel applies when it renders cards.
// A backend cut would blank the Price Box columns of every date it dropped, because the grid now
// feeds those columns from this same delivery.

import { ProtocolCommand } from '../../service-model/protocol-command.interface';
import { SHELL_PROTOCOL_OPERATIONS_NS } from '../../shell-communication/operations/shell-protocol-namespace.const';
import { AdjustmentPricingSettingsDto } from '../../shell-communication/shell-operations-protocol';
import { CashFlowAdjustment } from '../model/CashFlowAdjustment';

// Wire version of the RunApgOrionSkill contract (backend RunApgOrionSkill.ContractVersion).
// Documentary on the backend; bumped only in lockstep with a contract A1 change, which is the
// leader's change control (contract M1 §11).
export const APG_ORION_CONTRACT_VERSION = 1;

// The ESS message type the FE subscribes to (= the backend class name). Already registered on all
// three routing sites by the CR-20260910-001 foundation pass — the throttle pair in
// `ess-worker/throttler/` and the batch fan-out in `event-stream-service-client.service.ts`.
export const APG_ORION_CARDS_READY_MESSAGE_TYPE = 'ApgOrionCardsReadyDto';

// ---- Outcomes (backend ApgOrionRunOutcomes, contract A1 §5) ---------------------------------

export const APG_ORION_OUTCOME_SUCCEEDED = 'succeeded';
export const APG_ORION_OUTCOME_EMPTY = 'empty';
export const APG_ORION_OUTCOME_SUPERSEDED = 'superseded';
export const APG_ORION_OUTCOME_TERMINAL_FAILED = 'terminalFailed';
export const APG_ORION_OUTCOME_PRE_SEND_BLOCKED = 'preSendBlocked';

// ---- Command (FE -> BE) ---------------------------------------------------------------------

/**
 * Run the APG ORION roll skill on the backend. ASYNC-FIRE, as the two copilots: the call returns
 * an ack while the run executes off-thread, and the result arrives as ApgOrionCardsReadyDto over
 * the event stream, correlated by runId. TerminalId is resolved SERVER-side and never sent.
 *
 * Entitlement is the APG CoPilot's EXISTING secure element, reused — the migration mints no new
 * grant.
 *
 * ⚠ ARGUMENTS ARE POSITIONAL. A new one is APPENDED, never inserted: `buildRunApgOrionCommand`
 * passes them positionally, so a member placed mid-list would silently misalign every following
 * one. The same hazard the OPG's command records.
 */
export class RunApgOrionSkill implements ProtocolCommand {
    constructor(
        public readonly contractVersion: number,
        public readonly runId: string,
        public readonly panelInstanceId: string,
        public readonly portfolioId: string,
        // The expanded template settings, one per strategy, each with its flat before-state —
        // exactly what GetAdjustmentSolutions carries today. The host sets customDates to the
        // resolved window and the three look-forward counts to zero, as the APG ORION does now.
        public readonly adjustmentPricingSettings: AdjustmentPricingSettingsDto[],
        public readonly dateKind: string,              // "single" | "range"
        public readonly date: string | null,           // single: 'yyyy-MM-dd'
        public readonly rangeStart: string | null,     // range: 'yyyy-MM-dd'
        public readonly rangeEnd: string | null,       // range: 'yyyy-MM-dd'
        // The questionnaire's three lists. IGNORED by the backend for a single date (BR-W2) — they
        // are sent as the trader set them rather than pre-emptied here, so the wire carries the
        // questionnaire and the RULE stays in one place.
        public readonly weekdayExclusions: string[],
        public readonly specificDateExclusions: string[],
        // Days brought back from the weekday rule ('yyyy-MM-dd') - any excluded weekday since
        // CR-20260915-002 (was mondayOverrides). Mirrors RunApgOrionSkill.WeekdayOverrides.
        public readonly weekdayOverrides: string[],
        // S10 pass-through, unused in iteration 1 — the panel's bias control is visible and inert.
        // Carried so the shape does not change when it wakes.
        public readonly directionalBias: object | null = null
    ) {}

    getOperationName(): string {
        return SHELL_PROTOCOL_OPERATIONS_NS + '.RunApgOrionSkill';
    }
}

/**
 * The closed refusal list (outcome 'preSendBlocked', backend ApgOrionRefusals). A code outside
 * this list is a DEFECT, not an unknown state — the client surfaces it verbatim rather than
 * mapping it onto a friendlier neighbour.
 */
export const APG_ORION_REFUSALS = {
    missingPortfolio: 'MissingPortfolio',
    noPositions: 'NoPositions',
    malformedSettings: 'MalformedSettings',
    missingDate: 'MissingDate',
    rangeExceedsCap: 'RangeExceedsCap'
};

// ---- Delivery (BE -> FE, ESS push; camelCase as observed on the FE) --------------------------

/** The after-state's short strike per side. Either may be absent (backend `double?`). */
export interface ApgOrionShortStrikesDto {
    call: number | null;
    put: number | null;
}

/**
 * One delivered date.
 *
 * ⚠ WHAT IT DOES NOT CARRY, AND WHERE THE CARD GETS IT INSTEAD. There is no current strike, no
 * destination strike, no direction, no template name, no raw expiration and no pretty expiration
 * on this shape. All six are read off the card's OWN `adjustments[0]` by the client's projection —
 * the same fields, from the same object, that `readCandidates()` reads off a Price Box cell today.
 * Duplicating them on the wire would be a second source for one fact.
 */
export interface ApgOrionCardDto {
    date: string;                        // 'yyyy-MM-dd' chain key
    dte: number;                         // CALENDAR days, floored, from the SHELL clock or the QG cursor
    adjustmentTypes: string[];
    // The adjustment pop-up's input, with before and after state, exactly as the pop-up consumes
    // it. ONE adjustment per date by the zone rule (A-G2); both only when the portfolio has no
    // zone, with the ZoneUnavailable warning.
    adjustments: CashFlowAdjustment[];
    shortStrikes: ApgOrionShortStrikesDto | null;
    atm: number;
    perDayCredit: number | null;         // price / calendar DTE, floored; null when DTE <= 0
    price: number | null;                // the strategy-code price, TAKEN AS DELIVERED (R-A); null = unpriced, never zero
    reason: string;                      // the card's own reason line, ported verbatim
    economicReports: string[];
    tier2Returned: boolean;              // the lane rule restored this day
    unknown: boolean;                    // the calendar had no verdict — offered, but never reads as clear (A-G4)
    tier: string;                        // the day's own verdict in the CALENDAR's vocabulary: 'T1' | 'T2' | 'clear' | 'unknown' (CR-20260921-001) — NOT Maven's 'tier1'/'tier2'
}

/**
 * ONE WINDOW CELL THAT PRODUCED NO CARD, with its priced adjustment (leader ruling L37).
 *
 * ⚠ THIS IS NOT `Excluded[]` COMING BACK, and the difference is the whole of R-C. `Excluded[]`
 * carried a Date, a TIER and a REASON — a report of why a day was dropped, which the trader would
 * sit and reconcile against the days he asked for. This carries the date and the priced cell the
 * grid already draws, and says NOTHING about why it is not a card. **No tier, no reason, no counts,
 * ever**: a field like that here would break R-C.
 *
 * ⚠ PER CELL, NOT PER DATE — `date` IS NOT UNIQUE (leader ruling L50). A portfolio with NO ZONE
 * prices BOTH #2B and #3 for the same date (guarantee A-G2, with the `ZoneUnavailable` warning), so
 * that date owns two cells and one may become a card while the other does not. **Never key this
 * array by date**: a `Map`, a dictionary or a `find(x => x.date === d)` looks correct, passes every
 * ordinary run, and silently drops one Price Box column ONLY on a no-zone portfolio — the exact
 * state that already shows a warning, so the missing column reads as part of that condition rather
 * than as the defect it is. Concatenate; never de-duplicate.
 */
export interface ApgOrionWindowAdjustmentDto {
    date: string;                        // 'yyyy-MM-dd' chain key. NOT unique across the array.
    adjustments: CashFlowAdjustment[];   // the priced adjustment(s) for this cell
    price: number | null;                // the strategy-code price, as delivered (R-A); null = unpriced
}

/**
 * The run delivery — ONE push per run.
 *
 * ⚠ NO Excluded[] AND NO Coverage BLOCK (ruling R-C). Nothing about a dropped, excluded, filtered
 * or unpriced day reaches the user: no coverage note, no counts, no reasons. Those are written to
 * the BACKEND log instead (guarantee A-G5). The one exception is the `empty` outcome, whose
 * failureDetail carries a single sentence saying the run produced nothing at all — a blank panel
 * otherwise reads as a failure.
 *
 * ⚠ NO TIME STAMP. The cards re-price live from the panel's own strategy-code subscriptions and
 * show no snapshot moment.
 */
export interface ApgOrionCardsReadyDto {
    runId: string;
    outcome: string;                     // APG_ORION_OUTCOME_*
    failureDetail: string | null;        // plain message for the failure outcomes and the refusals
    cards: ApgOrionCardDto[];            // ranked; EVERY calculated date, no backend cap (A-G3)
    warnings: string[];                  // ZoneUnavailable and any other pricing warning

    /**
     * THE OTHER HALF OF THE PRICE BOX'S FEED (L37): every window cell that produced no card.
     *
     * A Monday or a report day never becomes a card, yet its column is on screen today with its
     * credit in it. Feeding the grid from `cards` alone would have silently emptied exactly those
     * columns — and milestone 1's gate is a PARITY smoke, so it would have failed there.
     *
     * ⚠ IT SHIPS ON AN `empty` RUN TOO. A single report day yields zero cards and its column must
     * still be drawn, so the Price Box feed is never gated on cards existing.
     */
    windowAdjustments: ApgOrionWindowAdjustmentDto[];
}
